import React, { useCallback, useState, useEffect } from "react";
import {
  View,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  Linking,
} from "react-native";
import { useFocusEffect } from "@react-navigation/native";
import FastImage from "react-native-fast-image";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import t from "@constants/localization";
import { actions as walletActions } from "@reducers/walletReducer";
import { useDispatch, useSelector } from "react-redux";
import MainTransactionCard from "./components/MainTransactionCard";
import { cancelDispute } from "@services/api";
import ToastMessage from "@components/Toast";
import Play from "@assets/svg/Play";
import color from "@constants/color";
import Text from "@components/CustomText";
import SkeletonLoaderList from "@components/SkeletonLoaderList";
import CancelDisputeModal from "./components/CancelDisputeModal";

const PATH = "screens/Wallet/v2/ViewDispute";

interface ViewDisputeProps {
  navigation: any;
  route: any;
}

const ViewDispute: React.FC<ViewDisputeProps> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const { dispute, isLoading } = useSelector((state) => state.walletReducer);
  const [showCancelDisputeModal, setShowCancelDisputeModal] = useState(false);
  const [attachmentsWithMime, setAttachmentsWithMime] = useState([]);
  const txnData = route?.params?.data || {};
  const txn_id = txnData?.id || "";
  const disputeData: any = dispute;

  useFocusEffect(
    useCallback(() => {
      dispatch(walletActions.getDispute(txn_id));
    }, [dispatch]),
  );

  useEffect(() => {
    const fetchMimeTypes = async () => {
      const attachmentsWithTypes = await Promise.all(
        disputeData?.attachments.map(async (uri) => {
          const mimetype = await getMimeType(uri);
          return { uri, mimetype };
        }),
      );
      setAttachmentsWithMime(attachmentsWithTypes);
    };

    if (disputeData?.attachments) {
      fetchMimeTypes();
    }
  }, [disputeData?.attachments]);

  const getMimeType = async (url) => {
    try {
      const response = await fetch(url, { method: "HEAD" });
      const contentType = response.headers.get("Content-Type");
      return contentType;
    } catch (error) {
      console.error("Error fetching MIME type:", error);
      return null;
    }
  };

  const handleCancel = async () => {
    try {
      await cancelDispute({ txn_id });
      ToastMessage("simple", "Dispute Cancelled", 3000, "bottom");
      navigation.goBack();
    } catch (err) {
      console.log(err);
      ToastMessage("simple", "Error cancelling dispute", 3000, "bottom");
    } finally {
      dispatch(walletActions.getDispute(txn_id));
      setShowCancelDisputeModal(false);
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case "APPROVED":
        return color.plain_green;
      case "CREATED":
        return color.pendingText;
      case "HOLD":
        return color.pendingText;
      case "REJECTED":
        return color.errorRed;
      default:
        return color.errorRed;
    }
  };

  return (
    <>
      <HeaderWithBackAndTitle
        route={route}
        navigation={navigation}
        headertitle={t(`${PATH}/wallet`)}
        subHeaderTitle={undefined}
        headerStyle={undefined}
        rightIcon={undefined}
        RightButton={undefined}
        goingBackHandler={undefined}
        IsRightIconDropDown={undefined}
      />
      {isLoading ? (
        <SkeletonLoaderList length={6} marginHorizontal={16} />
      ) : (
        <View style={styles.container}>
          <View
            style={{
              flexDirection: "row",
              marginHorizontal: 16,
              marginTop: 16,
              justifyContent: "space-between",
            }}
          >
            <View
              style={{ flexDirection: "row", flex: 1, alignItems: "center" }}
            >
              <Text variant="subtitle_20">{t(`${PATH}/dispute`)}</Text>
              {disputeData?.status_txt ? (
                <View
                  style={[
                    styles.statusBadge,
                    { backgroundColor: getStatusColor(disputeData?.status) },
                  ]}
                >
                  <Text variant="caption_2" style={styles.statusText}>
                    {t(`${PATH}/status`)} : {disputeData.status_txt}
                  </Text>
                </View>
              ) : null}
            </View>
            {["CREATED", "HOLD"].includes(disputeData?.status) ? (
              <TouchableOpacity
                style={styles.statusBadge}
                onPress={() => setShowCancelDisputeModal(true)}
              >
                <Text variant="caption_2" style={styles.statusText}>
                  {t(`${PATH}/cancelDispute`)}
                </Text>
              </TouchableOpacity>
            ) : null}
          </View>

          {disputeData?.resolved_at_txt ? (
            <View
              style={{
                marginHorizontal: 16,
                marginVertical: 8,
              }}
            >
              <Text variant="body_text_3" style={styles.disputeDate}>
                {disputeData.resolved_at_txt}
              </Text>
            </View>
          ) : null}

          {disputeData?.remarks ? (
            <View style={styles.reasonContainer}>
              <Text variant="caption_1" style={styles.reasonLabel}>
                {t(`${PATH}/reason`)}:
              </Text>
              <Text variant="caption_2" style={styles.reasonText}>
                {disputeData.remarks}
              </Text>
            </View>
          ) : null}

          <MainTransactionCard
            disableCta={true}
            item={txnData}
            onPress={() => {}}
          />

          {Array.isArray(attachmentsWithMime) ? (
            <View style={{ marginHorizontal: 16, marginBottom: 8 }}>
              <Text variant="body_text_2" style={styles.proofLabel}>
                {t(`${PATH}/proof`)}
              </Text>
              <View style={{ flexDirection: "row" }}>
                {attachmentsWithMime.map(({ uri, mimetype }) => {
                  if (mimetype?.startsWith("image/")) {
                    return (
                      <TouchableWithoutFeedback
                        onPress={() => {
                          if (uri) {
                            Linking.openURL(uri);
                          }
                        }}
                      >
                        <FastImage
                          key={uri}
                          style={{
                            width: 50,
                            height: 50,
                            marginRight: 15,
                            backgroundColor: "#fff",
                          }}
                          resizeMode="contain"
                          source={{
                            uri,
                          }}
                        />
                      </TouchableWithoutFeedback>
                    );
                  } else if (mimetype?.startsWith("audio/")) {
                    return (
                      <TouchableWithoutFeedback
                        onPress={() => {
                          if (uri) {
                            Linking.openURL(uri);
                          }
                        }}
                      >
                        <View
                          style={{
                            width: 50,
                            height: 50,
                            marginRight: 15,
                            backgroundColor: "#fff",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <Play />
                        </View>
                      </TouchableWithoutFeedback>
                    );
                  }
                })}
              </View>
            </View>
          ) : null}

          <View style={{ marginHorizontal: 16, marginTop: 16 }}>
            <Text variant="body_text_2">{t(`${PATH}/causeofDispute`)}:</Text>
            <View style={styles.disputeReasonContainer}>
              <Text style={styles.disputeReasonText}>
                {disputeData?.description}
              </Text>
            </View>
          </View>
        </View>
      )}
      <CancelDisputeModal
        onPress={handleCancel}
        visible={showCancelDisputeModal}
        onPressCancel={() => setShowCancelDisputeModal(false)}
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#f9f9f9",
    flex: 1,
  },
  statusBadge: {
    backgroundColor: color.errorRed,
    borderRadius: 16,
    paddingHorizontal: 10,
    marginLeft: 8,
    marginBottom: 8,
    padding: 4,
    paddingTop: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  disputeDate: {
    color: color.mediumBlack,
  },
  reasonContainer: {
    margin: 16,
    marginTop: 0,
    padding: 4,
    borderWidth: 1,
    borderColor: color.errorRed,
    borderRadius: 5,
    backgroundColor: "#fff4f4",
    flexDirection: "row",
    alignItems: "flex-start",
    paddingHorizontal: 12,
  },
  reasonLabel: {
    color: color.errorRed,
  },
  reasonText: {
    color: color.mediumBlack,
    lineHeight: 21,
    marginLeft: 4,
    flex: 1,
  },

  proofLabel: {
    marginBottom: 8,
  },
  disputeReasonContainer: {
    marginTop: 8,
    padding: 10,
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 5,
    backgroundColor: "#fff",
  },
  disputeReasonText: {
    color: color.mediumBlack,
  },
  statusText: {
    color: color.white,
  },
});

export default ViewDispute;
